import React, { useState, useEffect, useRef } from 'react'
import Typeahead from 'react-typeahead-component-with-vengence'
import _throttle from 'lodash.throttle'
import PropTypes from 'prop-types'

import I18n from 'lib/i18n'
import { categoryAutocompletePath } from 'lib/utils/links'
import HighlightText from 'lib/utils/highlight-text'

const SlugAutocomplete = ({
  showCount = false,
  autoFocus = false,
  withSmallPart = false,
  className = '',
  placeholder = '',
  inputId,
  fetchOptionsParent,
  inputValueParent,
  handleFocusParent,
  isLocationFilterAutoComplete,
  isMessageLocationPreferenceAutoComplete,
  isRequestCategoryLocationAutoComplete,
  handleKeyDownParent,
  performSearchParent,
  handleOptionClickParent,
  handleOptionChangeParent,
  handleChangeParent,
  getOptionsParent,
  optionsParent,
  getMessageForIncomingOptionsParent
}) => {
  const [inputValue, setInputValue] = useState(inputValueParent)
  const [oldInputValue, setOldInputValue] = useState('')
  const [optionsChild, setOptionsChild] = useState(null)
  const [lastQuery, setLastQuery] = useState('')
  const [inputClicked, setInputClicked] = useState(false)
  const searchRef = useRef(null)

  useEffect(() => {
    const spans = document.querySelectorAll('.filter-location-autocomplete span, .search-input-field span')
    if (spans.length && I18n.locale === 'ar') {
      for (let index = 0; index < spans.length; index++) {
        spans[index].style.left = 'unset'
        spans[index].style.right = '-9999px'
      }
    }
  }, [])

  useEffect(() => {
    setInputValue(inputValueParent)
  }, [inputValueParent])

  useEffect(() => {
    if (!optionsParent) {
      setOptionsChild([])
    }
  }, [optionsParent])

  const fetchOptions = async (query, callback) => {
    if (fetchOptionsParent) {
      fetchOptionsParent(query, callback)
    } else {
      try {
        const response = await fetch(categoryAutocompletePath(query))
        const data = await response.json()
        callback(data)
      } catch (error) {
        console.error(error)
      }
    }
  }

  const unthrottledGetOptions = query => {
    if (getOptionsParent) {
      getOptionsParent(query)
    } else {
      setLastQuery(query)
      fetchOptions(query, results => {
        if (query !== lastQuery) {
          setOptionsChild(results)
        }
      })
    }
  }

  const throttledGetOptions = _throttle(unthrottledGetOptions, 100)

  const handleChange = event => {
    if (handleChangeParent) {
      const value = event.target.value
      if (isMessageLocationPreferenceAutoComplete || isRequestCategoryLocationAutoComplete) {
        setInputValue(value)
      }
      handleChangeParent(event)
      throttledGetOptions(value)
    } else {
      const value = event.target.value
      setInputValue(value)
      throttledGetOptions(value)
    }
  }

  const handleOptionChange = (event, option) => {
    if (handleOptionChangeParent) {
      handleOptionChangeParent(event, option)
    } else {
      option && setInputValue(option.name)
    }
  }

  const handleFocus = () => {
    if (handleFocusParent) {
      setInputClicked(true)
      handleFocusParent()
    } else {
      setInputValue('')
      setOldInputValue(inputValue)
    }
  }

  const handleBlur = () => {
    if (
      !isLocationFilterAutoComplete &&
      !isMessageLocationPreferenceAutoComplete &&
      !isRequestCategoryLocationAutoComplete &&
      !inputValue &&
      oldInputValue
    ) {
      setInputValue(oldInputValue)
      setOldInputValue('')
    }
  }

  const performSearch = (query, data) => {
    if (performSearchParent) {
      performSearchParent(query, data)
    } else {
      console.warn(query, data)
    }
  }

  const startSearch = (query, data) => {
    const input = searchRef.current.refs.input
    input.blur()
    performSearch(query, data)
  }

  const handleKeyDown = (event, option, selectedIndex) => {
    if (handleKeyDownParent) {
      handleKeyDownParent(event, option, selectedIndex)
    } else {
      if (event.keyCode === 13) {
        if (selectedIndex >= 0) {
          startSearch(option.name, option)
        } else {
          startSearch(option, '')
        }
      }
    }
  }

  const handleOptionClick = (event, option) => {
    if (handleOptionClickParent) {
      handleOptionClickParent(event, option)
    } else {
      if (option) {
        if (inputClicked) {
          setInputClicked(false)
        }
        setInputValue(option.name)
        startSearch(option.name, option)
      }
    }
  }

  const optionTemplate = ({ isSelected, userInputValue, data: { name, count } }) => {
    if (withSmallPart) {
      const parts = name.split(',')
      const firstPart = parts.shift()
      return (
        <div className={isSelected ? 'is-focused' : ''}>
          <HighlightText highlight={userInputValue}>{firstPart}</HighlightText> <small>{parts.join(',')}</small>
        </div>
      )
    } else {
      return (
        <div className={isSelected ? 'is-focused' : ''}>
          <HighlightText highlight={userInputValue}>{name}</HighlightText>{' '}
          {showCount && count > 0 ? <em>({count})</em> : ''}
        </div>
      )
    }
  }

  const getMessageForIncomingOptions = number => {
    if (getMessageForIncomingOptionsParent) {
      return getMessageForIncomingOptionsParent(number)
    } else {
      return number + ' suggestions are available. Use up and down arrows to select.'
    }
  }

  return (
    <Typeahead
      ref={searchRef}
      inputId={inputId}
      className={`${className} ${inputClicked ? 'input-clicked' : ''}`}
      inputValue={inputValue}
      options={optionsChild || optionsParent}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={handleChange}
      onOptionChange={handleOptionChange}
      onOptionClick={handleOptionClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      optionTemplate={optionTemplate}
      getMessageForIncomingOptions={getMessageForIncomingOptions}
    />
  )
}

SlugAutocomplete.propTypes = {
  optionsParent: PropTypes.array,
  autoFocus: PropTypes.bool,
  showCount: PropTypes.bool,
  withSmallPart: PropTypes.bool,
  isLocationFilterAutoComplete: PropTypes.bool,
  isMessageLocationPreferenceAutoComplete: PropTypes.bool,
  isRequestCategoryLocationAutoComplete: PropTypes.bool,
  inputId: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  inputValueParent: PropTypes.string,
  fetchOptionsParent: PropTypes.func,
  handleChangeParent: PropTypes.func,
  handleFocusParent: PropTypes.func,
  handleKeyDownParent: PropTypes.func,
  handleOptionClickParent: PropTypes.func,
  handleOptionChangeParent: PropTypes.func,
  performSearchParent: PropTypes.func,
  getOptionsParent: PropTypes.func,
  getMessageForIncomingOptionsParent: PropTypes.func
}

export default React.memo(SlugAutocomplete)
